<template>
  <div class="modal-container flexcol">
    <div
      class="modal-header flexrow xbetween ycenter px-3 py-2"
      :class="isEdit ? 'edit-monthly-meeting' : 'add-monthly-meeting'"
    >
      <div class="flexrow ycenter fs-s color-white">
        <i class="bi bi-calendar2-check fs-m me-2"></i>
        <span> {{ isEdit ? "Edit" : "Add" }} Monthly Meeting</span>
      </div>
      <ModalCloseButton :color="isEdit ? 'primary' : 'success'" />
    </div>
    <div class="modal-body p-md-4 p-3">
      <form
        class="user-form"
        @submit.prevent="onFormSubmit"
        v-bind:validation-schema="schoolMonthlyMeetingSchema"
      >
        <div class="flexrow xbetween flex-wrap yend">
          <div class="inputs-grid mb-3 flex-fill">
            <CustomDropdown
              v-if="SchoolOptions"
              inputId="SchoolProfileId"
              inputLabel="School"
              inputPlaceholder="Select School Name"
              :dropdownItems="SchoolOptions"
              :isFilterable="true"
              :isFormField="true"
              :isMandatory="true"
              :isDisabled="isEdit"
              @onChange="onSchoolChange"
            />
          </div>
        </div>
        <CustomSwitch
          inputId="IsOffline"
          activeLabel="Is Offline"
          :initialValue="schoolMonthlyMeetingForm.values.IsOffline"
          :activeValue="true"
          :inactiveValue="false"
          :isFormField="true"
          :isDisabled="isEdit"
          :isSmall="true"
          class="mb-1"
        />
        <div class="flexrow flex-wrap ystart gap-3 mb-3">
          <CustomMonthPicker
            inputId="CalendarMonth"
            inputLabel="Calendar Month"
            :initialValue="schoolMonthlyMeetingForm.values.CalendarMonth"
            :isMandatory="true"
            :isFormField="true"
            :isDisabled="isEdit"
          />
          <CustomDatetimePicker
            inputId="MeetingDatetime"
            inputLabel="Schedule"
            :initialValue="schoolMonthlyMeetingForm.values.MeetingDatetime"
            :isFormField="true"
            :isMandatory="true"
            :isDisabled="isEdit"
          />
          <CustomInput
            inputId="MeetingVenue"
            inputLabel="Venue"
            :initialValue="schoolMonthlyMeetingForm.values.MeetingVenue"
            :isFormField="true"
            :isMandatory="true"
            :isDisabled="isEdit"
          />
        </div>

        <CustomRadioButton
          inputId="MeetingStatusId"
          inputLabel="Meeting Status"
          :initialValue="schoolMonthlyMeetingForm.values.MeetingStatusId"
          :radioItems="getIdNamePairsFromEnum(MeetingStatusEnum)"
          :isMandatory="true"
          :isFormField="true"
          :isSmall="true"
        />
        <div class="mb-3">
          <CustomTextArea
            inputId="MeetingAgenda"
            inputLabel="Agenda"
            :initialValue="schoolMonthlyMeetingForm.values.MeetingAgenda"
            :numberOfRows="4"
            :isFormField="true"
            :isMandatory="true"
            :isDisabled="isEdit"
          />
        </div>
        <CustomCheckbox
          v-if="schoolUserOptions && schoolUserOptions.length > 0"
          inputId="Invitees"
          inputLabel="Select Invitees"
          :checkboxItems="schoolUserOptions"
          :isFormField="true"
          :isMandatory="!isEdit"
          :isSmall="true"
        />
        <CustomCheckbox
          v-if="schoolUserOptions && schoolUserOptions.length > 0"
          inputId="Attendees"
          inputLabel="Select Attendees"
          :checkboxItems="schoolUserOptions"
          :isMandatory="isEdit"
          :isFormField="true"
          :isSmall="true"
        />
        <div class="inputs-grid mb-5">
          <CustomDropdown
            v-if="schoolUserOptions && schoolUserOptions.length > 0"
            inputId="ModeratorId"
            inputLabel="Moderator"
            :initialValue="schoolMonthlyMeetingForm.values.ModeratorId"
            :dropdownItems="schoolUserOptions"
            :isFilterable="true"
            :isMandatory="true"
            :isFormField="true"
            :isDisabled="isEdit"
          />
        </div>
        <el-divider content-position="left">
          <div class="fs-s fw-bold color-secondary">
            {{ isEdit ? "Discussion Topics" : "Discussion Topics [OPTIONAL]" }}
          </div>
        </el-divider>
        <div class="ps-4">
          <div class="flexrow xend mb-3">
            <div class="button button-outline secondary-link xend" @click="onAddDiscussionTopics">
              <i class="bi bi-plus-circle me-2"></i>
              <span class="concept-solution-button">Add Discussion</span>
            </div>
          </div>
          <el-collapse
            v-if="
              schoolMonthlyMeetingForm.values.DiscussionTopics &&
              schoolMonthlyMeetingForm.values.DiscussionTopics.length > 0
            "
          >
            <el-collapse-item
              v-for="(discussionTopic, idx) in schoolMonthlyMeetingForm.values.DiscussionTopics"
            >
              <template #title>
                <div class="flexrow ycenter xbetween gap-2 w-100">
                  <div class="flexrow ycenter gap-2">
                    <div class="serial-number-circle small fs-xxs bg-color-info dark me-2">
                      {{ idx + 1 }}
                    </div>
                    <div class="fs-xs fw-bold color-info">{{ discussionTopic?.TopicTitle }}</div>
                  </div>

                  <IconTooltipButton
                    v-if="!isEdit"
                    iconName="trash"
                    iconColor="danger"
                    iconSize="s"
                    tooltipText="Delete Topic"
                    @onClick="onDeleteDiscussionTopic(idx)"
                  />
                </div>
              </template>
              <div class="inputs-grid mb-3">
                <CustomInput
                  :inputId="`DiscussionTopics[${idx}].TopicTitle`"
                  :initialValue="discussionTopic?.TopicTitle"
                  inputLabel="Topic Title"
                  :isFormField="true"
                  :isMandatory="true"
                />
                <CustomDropdown
                  v-if="schoolUserOptions && schoolUserOptions.length > 0"
                  :inputId="`DiscussionTopics[${idx}].OwnerId`"
                  inputLabel="Select Owner"
                  :initialValue="discussionTopic?.OwnerId ? discussionTopic.OwnerId : undefined"
                  :dropdownItems="schoolUserOptions"
                  :isFilterable="true"
                  :isFormField="true"
                />
              </div>
              <CustomTextArea
                :inputId="`DiscussionTopics[${idx}].AdditionalInformation`"
                :initialValue="
                  discussionTopic?.AdditionalInformation
                    ? discussionTopic.AdditionalInformation
                    : undefined
                "
                inputLabel="Additional Information"
                :numberOfRows="2"
                :isFormField="true"
                class="mb-3"
              />
              <div>
                <CustomRadioButton
                  :inputId="`DiscussionTopics[${idx}].TopicCategoryId`"
                  :initialValue="discussionTopic?.TopicCategoryId"
                  inputLabel="Topic Category"
                  :radioItems="getIdNamePairsFromEnum(MonthlyMeetingTopicEnum)"
                  :isMandatory="true"
                  :isFormField="true"
                  :isSmall="true"
                />
                <CustomRadioButton
                  :inputId="`DiscussionTopics[${idx}].OriginatorId`"
                  :initialValue="discussionTopic?.OriginatorId"
                  inputLabel="Originator"
                  :radioItems="getIdNamePairsFromEnum(OriginatorEnum)"
                  :isMandatory="true"
                  :isFormField="true"
                  :isSmall="true"
                />
              </div>
            </el-collapse-item>
          </el-collapse>
        </div>
        <div class="flexrow gap-3 my-3 pt-2">
          <button type="submit" class="button small" :class="isEdit ? `primary` : `success`">
            {{ !isEdit ? "Add Meeting" : "Update Meeting" }}
          </button>
          <div v-if="isEdit" class="button small danger-reverse" @click="closeModal">Cancel</div>
          <div
            v-else
            class="button small danger-reverse"
            @click="schoolMonthlyMeetingForm.resetForm()"
          >
            Reset
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script lang="ts">
import IconTooltipButton from "@/components/IconTooltipButton.vue";
import CustomCheckbox from "@/components/input/CustomCheckbox.vue";
import CustomDatetimePicker from "@/components/input/CustomDatetimePicker.vue";
import CustomDropdown from "@/components/input/CustomDropdown.vue";
import CustomInput from "@/components/input/CustomInput.vue";
import CustomInputNumber from "@/components/input/CustomInputNumber.vue";
import CustomMonthPicker from "@/components/input/CustomMonthPicker.vue";
import CustomRadioButton from "@/components/input/CustomRadioButton.vue";
import CustomSwitch from "@/components/input/CustomSwitch.vue";
import CustomTextArea from "@/components/input/CustomTextArea.vue";
import ModalCloseButton from "@/components/modals/ModalCloseButton.vue";
import { MeetingStatusEnum, MonthlyMeetingTopicEnum, OriginatorEnum } from "@/enums/aem";
import { getIdNamePairsFromEnum, showCustomMessage } from "@/methods/utils";
import { useFilterableCardsStore } from "@/stores/filterableCardsStore";
import { useModalStore } from "@/stores/modalStore";
import { useSchoolStore } from "@/stores/schoolStore";
import { useSessionStore } from "@/stores/sessionStore";
import {
  SchoolMeetingDiscussionTopic,
  SchoolMeetingDiscussionTopicInputs,
  SchoolMonthlyMeetingDisplay,
  SchoolMonthlyMeetingInputs,
  SchoolMonthlyMeetingUpdate,
} from "@/types/admin";
import { IdNamePair } from "@/types/global";
import { toTypedSchema } from "@vee-validate/yup";
import { storeToRefs } from "pinia";
import { useForm } from "vee-validate";
import { defineComponent, onBeforeMount, PropType, ref } from "vue";
import * as yup from "yup";

export default defineComponent({
  name: "ModalAddEditMonthlyMeeting",
  components: {
    CustomDropdown,
    CustomInput,
    CustomRadioButton,
    CustomInputNumber,
    CustomSwitch,
    CustomCheckbox,
    CustomMonthPicker,
    CustomDatetimePicker,
    CustomTextArea,
    IconTooltipButton,
    ModalCloseButton,
  },
  props: {
    cardData: { type: Object as PropType<SchoolMonthlyMeetingDisplay>, default: null },
    isEdit: { type: Boolean, default: false },
  },
  setup(props) {
    const sessionStore = useSessionStore();
    const { allApiVerified } = storeToRefs(sessionStore);
    const schoolStore = useSchoolStore();
    const { SchoolOptions, SchoolCardsReloadKey } = storeToRefs(schoolStore);
    const { getSchoolOptions } = schoolStore;
    const filterableCardsStore = useFilterableCardsStore();
    const { CardsReloadKey } = storeToRefs(filterableCardsStore);
    const modalStore = useModalStore();
    const { closeModal } = modalStore;
    const schoolUserOptions = ref<Array<IdNamePair> | null>(null);

    const schoolMonthlyMeetingSchema = toTypedSchema(
      yup.object().shape({
        SchoolProfileId: yup.number().required("*required"),
        CalendarMonth: yup.string().required("*required"),
        MeetingAgenda: yup.string().required("*required"),
        ModeratorId: yup.number().required("*required"),
        Invitees: yup.array(yup.number().required()).min(1, "At least one invitee is required"),
        Attendees: yup.array(yup.number()).notRequired(),
        IsOffline: yup.boolean().required("*required"),
        MeetingVenue: yup.string().required("*required"),
        MeetingDatetime: yup.string().required("*required"),
        MeetingStatusId: yup.number().required("*required"),
        MongoId: yup.string().nullable(),
        DiscussionTopics: yup.array().of(
          yup
            .object()
            .nullable()
            .shape({
              TopicSerialNo: yup.number().required("*required"),
              TopicTitle: yup.string().required("*required"),
              TopicCategoryId: yup.number().required("*required"),
              AdditionalInformation: yup.string().nullable(),
              LinkedSchoolQueryId: yup.string().nullable(),
              OriginatorId: yup.number().required("*required"),
              OwnerId: yup.number().nullable(),
            }),
        ),
      }),
    );
    const schoolMonthlyMeetingForm = useForm({ validationSchema: schoolMonthlyMeetingSchema });
    onBeforeMount(async () => {
      await getSchoolOptions();
      if (props.isEdit && props.cardData) {
        await onSchoolChange(props.cardData.School.Id);
        schoolMonthlyMeetingForm.setFieldValue("SchoolProfileId", props.cardData.School.Id);
        schoolMonthlyMeetingForm.setFieldValue("CalendarMonth", props.cardData.CalendarMonth);
        schoolMonthlyMeetingForm.setFieldValue("MeetingAgenda", props.cardData.MeetingAgenda);
        schoolMonthlyMeetingForm.setFieldValue("MongoId", props.cardData.MongoId);
        schoolMonthlyMeetingForm.setFieldValue("ModeratorId", props.cardData.Moderator.Id);
        schoolMonthlyMeetingForm.setFieldValue(
          "Invitees",
          props.cardData.Invitees.map((invitee) => invitee.Id),
        );
        schoolMonthlyMeetingForm.setFieldValue(
          "Attendees",
          props.cardData.Attendees ? props.cardData.Attendees.map((attendee) => attendee.Id) : [],
        );
        schoolMonthlyMeetingForm.setFieldValue("IsOffline", props.cardData.IsOffline);
        schoolMonthlyMeetingForm.setFieldValue("MeetingDatetime", props.cardData.MeetingDatetime);
        schoolMonthlyMeetingForm.setFieldValue("MeetingStatusId", props.cardData.MeetingStatusId);
        schoolMonthlyMeetingForm.setFieldValue("MeetingVenue", props.cardData.MeetingVenue);
        schoolMonthlyMeetingForm.setFieldValue(
          "DiscussionTopics",
          props.cardData.DiscussionTopics as Array<SchoolMeetingDiscussionTopic>,
        );
      }
    });

    const onFormSubmit = schoolMonthlyMeetingForm.handleSubmit(onValidSubmit, onInvalidSubmit);

    async function onValidSubmit() {
      let response;
      if (props.isEdit) {
        response = await allApiVerified.value.schoolProfileApi.updateSchoolMonthlyMeeting(
          schoolMonthlyMeetingForm.values as SchoolMonthlyMeetingUpdate,
        );
      } else {
        response = await allApiVerified.value.schoolProfileApi.scheduleSchoolMonthlyMeeting(
          schoolMonthlyMeetingForm.values as SchoolMonthlyMeetingInputs,
        );
      }
      if (response.isSuccess) {
        showCustomMessage({
          messageText: "Monthly meetings form saved successfully.",
          messageType: "success",
        });
        schoolMonthlyMeetingForm.resetForm();
        closeModal();
        CardsReloadKey.value += 1;
        SchoolCardsReloadKey.value += 1;
      } else {
        showCustomMessage({ messageText: response.errorMessage, messageType: "error" });
      }
    }
    function onInvalidSubmit() {
      showCustomMessage({
        messageText: "Oops, there seem to be some errors in the form.",
        messageType: "error",
      });
    }

    async function onSchoolChange(selectedValue: number) {
      schoolUserOptions.value = null;
      const response = await allApiVerified.value.schoolProfileApi.getSchoolUsersForSchool(
        selectedValue,
      );
      const schoolUsers = response.isSuccess ? (response.data as Array<IdNamePair>) : [];
      schoolUserOptions.value = Object.values(schoolUsers).filter((user) => user !== null);
    }

    function onAddDiscussionTopics() {
      const discussionTopics = schoolMonthlyMeetingForm.values.DiscussionTopics ?? [];
      const updatedTopics = [...discussionTopics];
      const newDiscussionTopics = {
        TopicSerialNo: updatedTopics.length + 1,
      } as SchoolMeetingDiscussionTopicInputs;
      updatedTopics.push(newDiscussionTopics);
      schoolMonthlyMeetingForm.setFieldValue("DiscussionTopics", updatedTopics);
    }

    function onDeleteDiscussionTopic(topicIndex: number) {
      const currentTopics = schoolMonthlyMeetingForm.values.DiscussionTopics;
      if (!currentTopics) {
        return;
      }
      // TODO: Check if schoolMonthlyMeetingForm.values.DiscussionTopics can be modified directly
      const updatedTopics = [...currentTopics];
      updatedTopics.splice(topicIndex, 1);
      schoolMonthlyMeetingForm.setFieldValue("DiscussionTopics", updatedTopics);
    }

    return {
      OriginatorEnum,
      MonthlyMeetingTopicEnum,
      MeetingStatusEnum,
      SchoolOptions,
      schoolUserOptions,
      schoolMonthlyMeetingSchema,
      schoolMonthlyMeetingForm,
      getIdNamePairsFromEnum,
      onAddDiscussionTopics,
      onDeleteDiscussionTopic,
      onSchoolChange,
      onFormSubmit,
      closeModal,
    };
  },
});
</script>

<style lang="scss" scoped>
.modal-container {
  width: 80%;
  max-height: 90vh;
  background-color: $color-white;
  border-radius: 10px;
  @include media("<=s") {
    width: 95%;
  }
  & .modal-header {
    background-color: $color-success;
    &.edit-monthly-meeting {
      background-color: $color-primary-light;
    }
  }
  & .modal-body {
    overflow: auto;
    & .inputs-grid {
      display: grid;
      grid-template-columns: repeat(auto-fill, 320px);
      column-gap: 10px;
      overflow: hidden;
      @include media("<=s") {
        grid-template-columns: repeat(auto-fill, 240px);
      }
      @include media("<=xs") {
        grid-template-columns: repeat(auto-fill, 200px);
      }
    }
  }
}
</style>
